import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../css/EnterpriseDetails.css";
import filterIcon from "../../../assets/Images/Filter.png";
import changeStatusIcon from "../../../assets/icons/changeStatusIcon.png";
import deleteIcon from "../../../assets/icons/deleteIcon.png";
import trainDoc from "../../../assets/icons/TrainDoc.png";
import trainText from "../../../assets/icons/TrainText.png";
import trainUrls from "../../../assets/icons/TrainUrl.png";
import trainVideo from "../../../assets/icons/TrainVideo.png";
import trainQA from "../../../assets/icons/TrainQA.png";
import { getFilteredDocs } from "../../../utils/approvedContentTabs";
import { formatDateToUserTimeZone } from "../../../utils/formatDate";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { useTableSort } from "../../../hooks/useTableSort";
import { SortableHeader } from "../../../components/SortableHeader";
import viewIcon from "../../../assets/UserIcons/ViewIcon.svg";
import crossIcon from "../../../assets/UserIcons/Cancel.svg";

const TextHistory = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [uploadedText, setUploadedText] = useState([]);
  const [allApprovedDocs, setAllApprovedDocs] = useState([]);
  const [uploadedQA, setUploadedQA] = useState([]);
  const [viewTextContent, setViewTextContent] = useState("");
  const [showViewTextModal, setShowViewTextModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname;

  const tabs = [
    { name: "Documents", path: "/admin/knowledge-repository" },
    { name: "Web URLs", path: "/admin/knowledge-repo-urls" },
    { name: "Video URLs", path: "/admin/knowledge-repo-video" },
    { name: "Texts", path: "/admin/knowledge-repo-text" },
    { name: "Q&A", path: "/admin/knowledge-repo-qa" },
  ];

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    fetchApprovedDocs(); // docs, urls, videos
    fetchTexts(); // text
    fetchQAs(); // q&a
  }, []);

  const fetchTexts = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/text`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setUploadedText(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch uploaded texts:", err);
      // showErrorToast("Error loading uploaded text");
    }
  };

  const fetchApprovedDocs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/get-enterprise-docs`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAllApprovedDocs(res.data);
    } catch (error) {
      console.error("❌ Error fetching approved docs:", error);
    }
  };

  const fetchQAs = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/qa`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUploadedQA(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch uploaded QAs:", err);
    }
  };

  const documentFiles = getFilteredDocs(allApprovedDocs, "document");
  const urlFiles = getFilteredDocs(allApprovedDocs, "webpage").map((w) => ({
    ...w, // keep original fields like pythonFileUuid, status, etc.
    name: w.name,
    characters: w.size || 0,
    updatedDate: w.updatedAt,
    createdDate: w.createdAt,
    action: "View",
  }));
  const youtubeFiles = getFilteredDocs(allApprovedDocs, "youtube_audio").map(
    (w) => ({
      ...w, // keep all fields like pythonFileUuid, status
      name: w.name,
      characters: w.size || 0,
      updatedDate: w.updatedAt,
      createdDate: w.createdAt,
      action: "View",
    }),
  );

  const filteredFiles = uploadedText
    .filter((file) =>
      file.text_content?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((file) => {
      if (!dayFilter) return true;
      const updatedDate = new Date(file.updated_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return updatedDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return updatedDate >= yesterday && updatedDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredFiles.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredFiles.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleViewText = (item) => {
    setViewTextContent(item.text_content || "");
    setShowViewTextModal(true);
  };

  const statsData = [
    {
      label: "Uploaded Documents",
      value: documentFiles.length,
      icon: trainDoc,
    },
    { label: "Uploaded Web URLs", value: urlFiles.length, icon: trainUrls },
    {
      label: "Uploaded Video URLs",
      value: youtubeFiles.length,
      icon: trainVideo,
    },
    { label: "Uploaded Texts", value: uploadedText.length, icon: trainText },
    { label: "Uploaded Q&A", value: uploadedQA.length, icon: trainQA },
  ];

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  // Prepare the data including the computed text_length
  const sortedDataWithLength = sortedData.map((item) => ({
    ...item,
    text_length: item.text_content?.length || 0,
  }));

  const handleChangeQAStatus = async (uploadId, currentStatus) => {
    const newStatus = currentStatus === "1" ? "0" : "1";

    console.log(uploadId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/update_qa_status/${uploadId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ upload_status: newStatus }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        showErrorToast(result.message || "Failed to update status");
        return;
      }

      showSuccessToast("Status updated successfully");

      // Refresh your QA list
      fetchTexts();
    } catch (err) {
      console.error("Error updating status:", err);
      showErrorToast("Something went wrong");
    }
  };

  return (
    <div className="mx-2 pt-1 h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 justify-content-evenly align-items-stretch">
        {statsData.map((item, index) => (
          <div
            key={index}
            className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between"
          >
            <div className="d-flex align-items-center mb-2">
              {item.icon && (
                <img
                  className="icon-image"
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                />
              )}
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>

            <h2 className="fw-bold">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold ak-text-lg">Uploaded Texts</span>
      </div>

      <div className="mt-3 mb-3 tabs-wrapper ">
        <div className="tab-container">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              style={{
                fontWeight: currentTab === tab.path ? "700" : "500", // inline desktop logic
              }}
              className={`tab-button ${
                currentTab === tab.path ? "active" : ""
              }`}
              onClick={() => navigate(tab.path)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {totalEntries === 0 ? (
        <></>
      ) : (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={handleEntriesChange}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="ms-2 mt-2">Entries per page</label>
          </div>

          <div className="d-flex align-items-center">
            <div className="custom-search-wrapper me-3">
              <div
                className="input-group custom-search-group"
                style={{ width: "320px" }}
              >
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Texts"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            <div className="">
              <button
                className="icon-button filter-dropdown-toggle action-button"
                type="button"
                data-bs-toggle="dropdown"
                title="Filter"
              >
                <img src={filterIcon} className="icon-image" alt="Filter" />
              </button>

              <div
                className="dropdown-menu profile-menu-dropdown p-2"
                style={{ minWidth: "200px" }}
              >
                <h6 className="fw-bolder refresh-prompts">Time</h6>
                <hr />
                {["asc", "desc"].map((order) => (
                  <div className="form-check" key={order}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sortOrder"
                      id={order}
                      value={order}
                      checked={sortOrder === order}
                      onChange={() => setSortOrder(order)}
                    />
                    <label className="form-check-label" htmlFor={order}>
                      {order === "asc" ? "Ascending" : "Descending"}
                    </label>
                  </div>
                ))}

                <hr />
                <h6 className="fw-bolderrefresh-prompts">By Day</h6>
                <hr />
                {["Today", "Yesterday", ""].map((day) => (
                  <div className="form-check" key={day || "all"}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="dayFilter"
                      id={day || "all"}
                      value={day}
                      checked={dayFilter === day}
                      onChange={() => setDayFilter(day)}
                    />
                    <label className="form-check-label" htmlFor={day || "all"}>
                      {day || "All"}
                    </label>
                  </div>
                ))}

                <hr />
                <div className="d-flex justify-content-between mt-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    className="btn dropdown-btn bg-dark text-white btn-sm"
                    onClick={() => setCurrentPage(1)}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">
            No text have been trained yet.
          </p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table trainingtext-table table-sm w-100">
              <thead className="text-left">
                <tr>
                  <SortableHeader
                    column="text_content"
                    label="Text"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="text_length"
                    label="Total Characters"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  {/* VKD68 started */}
                  <SortableHeader
                    column="upload_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  {/* VKD68 End */}
                  {/* <th>Status</th> */}

                  <SortableHeader
                    column="submitted_by"
                    label="Uploaded By"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="created_timestamp"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedDataWithLength.map((item, index) => (
                  <tr key={index}>
                    <td
                      className="text-truncate"
                      style={{ maxWidth: "300px" }}
                      title={item.text_content}
                    >
                      {item.text_content}
                    </td>
                    <td>{item.text_content?.length || 0}</td>
                    <td className="text-left">
                      <span
                        className={`badge ${
                          item.upload_status === "1"
                            ? "bg-success" // GREEN
                            : "bg-warning" // YELLOW
                        }`}
                      >
                        {item.upload_status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{item.submitted_by}</td>
                    <td className="text-left">
                      {formatDateToUserTimeZone(item.created_timestamp)}
                      {/* {new Date(file.createdAt).toLocaleString()} */}
                    </td>
                    <td className="text-left">
                      {formatDateToUserTimeZone(item.updated_timestamp)}
                      {/* {new Date(file.updatedAt).toLocaleString()} */}
                    </td>
                    <td>
                      <button
                        onClick={() => handleViewText(item)}
                        className="icon-button"
                        title="View Text"
                      >
                        <img
                          src={viewIcon}
                          className="icon-image"
                          alt="view Text"
                        />
                      </button>

                      <button
                        onClick={() =>
                          handleChangeQAStatus(
                            item.upload_id,
                            item.upload_status,
                          )
                        }
                        className="icon-button"
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {showViewTextModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-history modal-dialog-centered history-model">
            <div className="modal-content w-50 position-relative">
              {/* ❌ CLOSE BUTTON */}
              <button
                type="button"
                className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                onClick={() => {
                  setShowViewTextModal(false);
                  setViewTextContent("");
                }}
              >
                <img
                  src={crossIcon}
                  className="icon-image cross-i"
                  alt="Close"
                />
              </button>

              <p className="fw-bold mt-3 mb-3 text-center refresh-prompts">
                Uploaded Text Content
              </p>

              {/* 📄 CONTENT */}
              <div className="text-center p-3">
                <div
                  className="px-2"
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    textAlign: "left",
                    whiteSpace: "pre-wrap",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    padding: "10px",
                    backgroundColor: "#fafafa",
                    fontSize: "14px",
                    lineHeight: "1.5",
                  }}
                >
                  {viewTextContent}
                </div>
              </div>

              {/* FOOTER */}
              <div className="modal-footer justify-content-center">
                <button
                  type="button"
                  className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                  onClick={() => {
                    setShowViewTextModal(false);
                    setViewTextContent("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextHistory;
